"use client";

import React, { Fragment } from "react";
import Link from "next/link";
import { useI18n } from "@/lib/i18n";
import { usePageMeta } from "@/hooks/usePageMeta";
import SocialLinks from "@/components/SocialLinks";
import type { Block, Legal, LegalDocContent } from "@/lib/locales/legal/en";

const PROSE =
  "text-fg " +
  "[&_h2]:font-display [&_h2]:text-fg-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-3 " +
  "[&_h3]:text-fg-heading [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-1 " +
  "[&_p]:text-fg-subtle [&_p]:leading-relaxed [&_p]:my-3 " +
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_li]:text-fg-subtle [&_li]:my-1 " +
  "[&_strong]:text-fg-heading [&_a]:text-fg-link [&_a]:no-underline hover:[&_a]:underline";

type DocKey = "terms" | "privacy" | "cookie" | "refund" | "contact";

/** Fixed contact endpoints — brand/legal constants, not translated. */
const EMAIL = "info@skillscan.uk";
const PHONE = "+48 579 772 268";

/**
 * Replace {token} placeholders in a legal string with the right anchor.
 * Internal-link labels come from the active locale's `links` so they read
 * naturally in each language; email/phone/WayForPay are constants.
 */
function withLinks(text: string, links: Legal["links"]): React.ReactNode {
  const map: Record<string, React.ReactNode> = {
    wayforpay: (
      <a href="https://wayforpay.com/en" target="_blank" rel="noopener noreferrer">{links.wayforpay}</a>
    ),
    email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>,
    phone: <a href={`tel:${PHONE.replace(/\s/g, "")}`}>{PHONE}</a>,
    refund: <Link href="/refund-policy">{links.refund}</Link>,
    contact: <Link href="/contact">{links.contact}</Link>,
    terms: <Link href="/terms">{links.terms}</Link>,
    cookie: <Link href="/cookie-policy">{links.cookie}</Link>,
    privacy: <Link href="/privacy">{links.privacy}</Link>,
    // Negative margins line the first glyph up with the text and keep the row height close to its neighbours.
    social: <SocialLinks className="-ms-3 -my-2.5" />,
  };
  return text.split(/(\{[a-z]+\})/g).map((part, i) => {
    const m = part.match(/^\{([a-z]+)\}$/);
    if (m && map[m[1]]) return <Fragment key={i}>{map[m[1]]}</Fragment>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function renderBlock(block: Block, i: number, links: Legal["links"], privacyPolicyLabel: string) {
  if ("h" in block) return <h2 key={i}>{block.h}</h2>;
  if ("h3" in block) return <h3 key={i}>{block.h3}</h3>;
  if ("p" in block) return <p key={i}>{withLinks(block.p, links)}</p>;
  if ("ul" in block) {
    return (
      <ul key={i}>
        {block.ul.map((item, j) => <li key={j}>{withLinks(item, links)}</li>)}
      </ul>
    );
  }
  if ("dl" in block) {
    return (
      <dl key={i} className="my-4">
        {block.dl.map((row, j) => (
          <div
            key={j}
            className="flex flex-col gap-0.5 py-3 border-b border-line-subtle last:border-b-0 sm:flex-row sm:gap-6"
          >
            <dt className="text-fg-muted text-sm sm:w-64 sm:flex-none">{row.k}</dt>
            <dd className="text-fg-heading m-0">{withLinks(row.v, links)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  // providers list (cookie policy) — each item may carry an external privacy link
  return (
    <ul key={i}>
      {block.providers.map((prov, j) => (
        <li key={j}>
          {prov.text}
          {prov.href && (
            <>
              {" ("}
              <a href={prov.href} target="_blank" rel="noopener noreferrer">{privacyPolicyLabel}</a>
              {")"}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function LegalDoc({ docKey }: { docKey: DocKey }) {
  const { l } = useI18n();
  const doc: LegalDocContent = l[docKey];
  usePageMeta(doc.metaTitle, doc.metaDescription);

  return (
    <main className="min-h-screen bg-canvas text-fg">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link href="/" className="backlink">
          <span className="material-icons text-base" aria-hidden="true">arrow_back</span>
          {l.backHome}
        </Link>

        {doc.draft && (
          <div className="mt-4 mb-6 rounded-[var(--radius-md)] border border-line-subtle bg-surface border-l-2 border-l-warning px-4 py-3 text-sm text-warning">
            {doc.draft}
          </div>
        )}

        <h1 className={`display-small mb-2 ${doc.draft ? "" : "mt-4"}`}>{doc.title}</h1>
        {doc.updated && <p className="text-fg-muted text-sm">{doc.updated}</p>}
        {doc.intro && <p className="text-fg-muted text-sm">{doc.intro}</p>}

        <div className={PROSE}>
          {doc.blocks.map((b, i) => renderBlock(b, i, l.links, l.links.privacyPolicy))}
        </div>
      </div>
    </main>
  );
}
